/**
 * NAR / auth-letter row with delete control.
 *
 * On delete it identifies every J-code that currently carries the targeted
 * document (via {@link generateClearDocumentsPayloadsForDocId}) and sends one
 * `documents: []` payload per such J-code so the backend wipes the entire
 * document list for that J-code (not just the matching doc). Auth-on-file is
 * then refetched so the UI reflects the cleared state.
 *
 * @module narLetterDelete
 */
import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useMemo, useRef, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { getViewerDocumentTitleByDocType } from "../../../shared/constants/statusToDocMap/statusToDocMap";
import { DocWriteBackStatus } from "../../../shared/enums/evBvWriteBackStatus";
import { ScreenNamesForBigQuery } from "../../../shared/enums/eventsData";
import { Delete } from "../../../shared/svg/delete";
import DocIcon from "../../../shared/svg/doc-icon";
import { getDeleteDocumentEventNameWithFallback } from "../../utils/deleteDocumentEventTypeName";
import {
  insertDocumentDeletedFromOrderTrailComment,
  resolveJCodeLabelsForTrail,
} from "../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";

import { updateCPTtables } from "../../../shared/api/postCall/updateCPTtables";
import { updateAllJCodeDocuments } from "../../../shared/api/postgreSQL/postCalls/updateJCodeDocuments";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { getAuthLetterReviewStatusClearsForDocId } from "../../utils/updateJcodesWithCorrectDocRef";
import { generateClearDocumentsPayloadsForDocId } from "../modals/narLetterModal/utils/generateDocumentUpdatePayloads";

/** Props for {@link NarLetterDelete}. */
interface NarLetterDeleteProps {
  /** Label shown next to the document icon. */
  title: string;
  /** Called after a successful delete and exit animation. */
  onDelete?: () => void;
  /** Horizontal direction used for the exit transition class. */
  exitDirection?: "left" | "right";
  /** Document type string used for analytics. */
  docType?: string;
  /**
   * Document `id` whose presence on a J-code marks that J-code for clearing.
   * Every J-code that contains this `docId` will have its full `documents`
   * array reset to `[]` on the backend.
   */
  docId?: string;
  /** Optional handler when the title/icon row is clicked. */
  onClick?: () => void;
  /** EV-BV write-back status controlling delete visibility and styling. */
  docUploadStatus?: string;
}

/**
 * Renders a compact document row with optional delete when write-back is not in progress.
 */
const NarLetterDelete = ({
  title,
  onDelete,
  exitDirection = "left",
  docType,
  docId,
  onClick,
  docUploadStatus,
}: NarLetterDeleteProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { generalOrderDetails, authOnFileDetails, fetchAuthOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const { user } = useAuthSelector((state) => state.firebaseAuthentication);
  const { data: providerDetails } = useAuthSelector(
    (state) => state.providerDetails,
  );

  const controllerRef = useRef<AbortController | null>(null);

  /**
   * Logs a delete-document analytics event, sends one `documents: []` payload
   * per affected J-code (resolved against the *original* auth-on-file entries,
   * not a locally-mutated copy), clears `auth_letter_review_status` when the
   * removed doc is an auth letter, refetches auth-on-file, and triggers the
   * exit animation on success. Any in-flight request from a prior click is
   * aborted before the new one starts.
   *
   * @returns Promise that resolves after the delete attempt finishes (success or failure).
   */
  const handleDelete = async () => {
    setIsDeleting(true);
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      order_id: generalOrderDetails?.order?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() ?? "",
      event_name: getDeleteDocumentEventNameWithFallback(docType ?? ""),
      additional_data: {
        screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
        doc_type: docType ?? "",
      },
    });
    const originalAuthEntries =
      authOnFileDetails?.auth_on_file?.entries ??
      authOnFileDetails?.auth_on_file?.auth_entries ??
      [];

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const orderId = generalOrderDetails?.order?.order_id ?? "";
    const orgId = getOrgIdForFetchExternalWorklist() ?? "";
    const reviewStatusClears = getAuthLetterReviewStatusClearsForDocId(
      originalAuthEntries,
      docId ?? "",
    );

    const clearDocumentPayloads = generateClearDocumentsPayloadsForDocId(
      originalAuthEntries,
      orderId,
      orgId,
      docId ?? "",
    );
    const deletedJCodeLabels = resolveJCodeLabelsForTrail(
      clearDocumentPayloads.map((payload) => payload.j_code_id),
      originalAuthEntries,
    );

    let success = false;
    try {
      const docUpdateResult = await updateAllJCodeDocuments(
        clearDocumentPayloads,
        controller.signal,
      );
      success = docUpdateResult.success;

      if (success && reviewStatusClears.length > 0) {
        await updateCPTtables(
          { order_id: orderId, org_id: orgId, j_codes: reviewStatusClears },
          controller.signal,
        );
      }
    } catch (error) {
      if ((error as Error)?.name !== "AbortError") {
        console.error("Failed to delete document from J-code", error);
      }
    }

    await fetchAuthOnFileDetails(orderId, orgId);

    setIsDeleting(false);

    if (success) {
      const actingProviderName =
        `${providerDetails?.FirstName || ""} ${providerDetails?.LastName || ""}`.trim() ||
        "Unknown Provider";

      insertDocumentDeletedFromOrderTrailComment({
        orderId,
        orgId,
        patientId:
          generalOrderDetails?.order?.demographics?.patient_id ??
          generalOrderDetails?.order?.demographics?.patient_mrn,
        providerEmail: user?.email ?? "",
        providerName: actingProviderName,
        jCodeLabels: deletedJCodeLabels,
        documentLabel: getViewerDocumentTitleByDocType(docType ?? ""),
      });

      setIsExiting(true);
      setTimeout(() => {
        onDelete?.();
      }, 300);
    } else {
      console.error("Failed to delete document from J-code");
    }
  };

  const exitDirectionClass = useMemo(() => {
    return exitDirection === "left" ? "-translate-x-4" : "translate-x-4";
  }, [exitDirection]);
  return (
    <div
      className={`flex flex-row items-center justify-center gap-1 rounded-md bg-lightGray p-2 transition-all duration-300 ease-in-out ${isExiting ? `${exitDirectionClass} opacity-0` : "opacity-100"} `}
    >
      {isDeleting ? (
        <>
          <div className="flex flex-row items-center justify-center gap-2">
            <SpinningLoader />
            <div className="text-sm">Deleting...</div>
          </div>
        </>
      ) : (
        <>
          <div onClick={onClick} className="flex flex-row items-center gap-2">
            <div>
              <DocIcon />
            </div>
            <div
              className={`mr-1 border-gray-300 pr-2 text-tiny font-semibold ${docUploadStatus === DocWriteBackStatus.NOT_INITIATED ||
                  docUploadStatus === DocWriteBackStatus.ERROR
                  ? "border-r-2"
                  : ""
                }`}
            >
              {title}
            </div>
          </div>
          {docUploadStatus !== DocWriteBackStatus.IN_PROGRESS &&
            docUploadStatus !== DocWriteBackStatus.QUEUED && (
              <div className="icon cursor-pointer" onClick={handleDelete}>
                <Delete height={14} width={14} />
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default NarLetterDelete;
